import { useEffect, useMemo, type PropsWithChildren } from 'react'
import { useLocation } from 'react-router-dom'
import { replaceWithRegion } from 'actions/NavigationActions'
import { EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OhNoRefreshError from 'components/Common/ErrorBoundary/OhNoRefreshError'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { useTypeaheadExtractionRefresh } from 'hooks/Search/useTypeaheadExtractionRefresh'
import { getCompletedExperienceQuery, getExperienceNaturalLanguageParams } from 'lib/search/experienceNaturalLanguageSearch'
import { isTransferSearchParams } from 'lib/transfers/transferOfferSearchParams'

function ExperienceQueryExtraction({ query, typeaheadId }: { query: string, typeaheadId: string }) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const data = useAppSelector(state => state.search.typeaheadStreamingData[typeaheadId])
  const item = getCompletedExperienceQuery(data, query)
  const params = useMemo(() => item && getExperienceNaturalLanguageParams(item), [item])

  useTypeaheadExtractionRefresh({
    isExtracting: true,
    query,
    typeaheadId,
    typeaheadTypes: EXPERIENCE_TYPEAHEAD_SEARCH_TYPES,
  })

  useEffect(() => {
    if (!params || data?.streamingError) return
    const next = new URLSearchParams(location.search)
    next.delete('extracting')
    next.delete('semanticSearchQuery')
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) next.set(key, value)
    }
    dispatch(replaceWithRegion(location.pathname, next.toString(), location.hash?.slice(1) || undefined))
  }, [params, data?.streamingError, location.pathname, location.search, location.hash, dispatch])

  if (data?.streamingError || (item && !params)) {
    return <OhNoRefreshError>{query}</OhNoRefreshError>
  }
  return <LoadingIndicator />
}

export default function ExperienceSearchQueryBoundary({ children }: PropsWithChildren) {
  const queryParams = useQueryParams()
  // Transfers stay destination based and never run experience query extraction.
  if (isTransferSearchParams(queryParams)) return children
  if (queryParams.get('extracting') !== 'true') return children
  const query = queryParams.get('query')?.trim()
  const typeaheadId = queryParams.get('typeaheadId')
  if (!query || !typeaheadId) return <OhNoRefreshError>{query}</OhNoRefreshError>
  return <ExperienceQueryExtraction key={typeaheadId} query={query} typeaheadId={typeaheadId} />
}
